import { useAuth0 } from '@auth0/auth0-react';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Button } from '../../../../components/buttons';
import { RootState, useAppDispatch } from '../../../../reducers';
import { InviteCompanyUserListItemArgs, useInviteCompanyUserMutation } from '../../../../services/endpoints/companies';
import RouteConstants from '../../../router/RouteConstants';
import { setOnboard } from '../../../userInfo/userInfoSlice';

interface InviteCollaboratorsProps {
  back?: () => void;
}

const InviteCollaborators: FunctionComponent<InviteCollaboratorsProps> = ({ back }) => {
  const { t } = useTranslation('company');

  const dispatch = useAppDispatch();
  const { user } = useAuth0();
  const company = useSelector((state: RootState) => state.userInfo.company);
  const history = useHistory();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [collaborators, setCollaborators] = useState<string[]>(['', '', '']);
  const [contact, setContact] = useState<string>('');
  const [contactIsMe, setContactIsMe] = useState<boolean>(false);

  const [inviteCompanyUser, { data }] = useInviteCompanyUserMutation();

  useEffect(() => {
    if (data) {
      dispatch(
        setOnboard({
          forceCompanyCreation: false,
        })
      );
      history.push(RouteConstants.home);
    }
  }, [data]);

  const save = (event: any) => {
    event.preventDefault();

    const reqBody: InviteCompanyUserListItemArgs[] = collaborators
      .filter((collaborator) => !!collaborator.trim())
      .map((collaborator) => ({
        email: collaborator,
        role: 'ADMIN',
      }));
    if (contact && !contactIsMe) {
      reqBody.push({ email: contact, role: 'ACCOUNTING' });
    }

    if (reqBody.length) {
      for (const inviteCompanyUserListItemArg of reqBody) {
        inviteCompanyUser({
          companyId: company?.id as any,
          userList: inviteCompanyUserListItemArg,
        });
      }
    } else {
      dispatch(
        setOnboard({
          forceCompanyCreation: false,
        })
      );
      history.push(RouteConstants.home);
    }
  };

  return (
    <div className={'flex size-full items-center justify-center bg-gray-100'}>
      <div className={'flex flex-col items-center rounded-lg bg-white-100 p-8 shadow-md'} style={{ width: '448px' }}>
        <h3 className={'mb-5 text-xl font-semibold'}>{t('inviteYourClosestCollaborators')}</h3>
        <h3 className={'mb-1 mr-auto text-lg font-semibold'}>{t('admin_other')}</h3>
        <p className={'mb-4 text-sm'}>{t('theyHelpYouInTheManagementOdAudiencerateTheyCanCreateWorkspacesAndInviteOtherPeople')}</p>
        {errorMessage && <div className={'mb-3 w-full rounded border-red-300 bg-red-600 p-2 text-white-100'}>{errorMessage}</div>}
        <form className={'w-full'}>
          {collaborators.map((collaborator, index) => (
            <div key={index} className={'mb-4'}>
              <input
                className={'w-full rounded-md border-gray-300 shadow-sm'}
                type={'text'}
                value={collaborator}
                placeholder={`collaborator@${company?.domains[0]}`}
                readOnly={false}
                onChange={(event) => {
                  let newCollaboratorsList = collaborators.map((coll, i) => (i === index ? event.target.value : coll));

                  newCollaboratorsList = newCollaboratorsList || [];

                  const tempArray = [...newCollaboratorsList];
                  let i = tempArray.reverse().findIndex((coll) => !!coll.trim());
                  if (i > -1) {
                    i = tempArray.length - 1 - i;
                    if (i + 3 < newCollaboratorsList.length) {
                      newCollaboratorsList.splice(i + 3);
                    } else if (i + 3 > newCollaboratorsList.length) {
                      newCollaboratorsList = newCollaboratorsList.concat(new Array(i + 3 - newCollaboratorsList.length).fill(''));
                    }
                  }

                  setCollaborators(newCollaboratorsList);
                }}
              />
            </div>
          ))}
          <h3 className={'mb-1 mr-auto text-lg font-semibold'}>{t('accounting')}</h3>
          <p className={'mb-4 text-sm'}>
            {t('theyWillCompileTheLegalFieldsForYouAndBeAbleToDownloadThePdfsOfTheInvoicesWithoutBotheringYouAndDoTheOtherBureaucraticThings')}
          </p>
          <div className={'mb-4'}>
            <input
              className={'w-full rounded-md border-gray-300 shadow-sm'}
              type={'text'}
              value={contact}
              placeholder={`accounting@${company?.domains[0]}`}
              disabled={contactIsMe}
              onChange={(event) => setContact(event.target.value)}
            />
          </div>
          <div className={'mb-8'}>
            <label>
              <input
                className={'mr-2'}
                type={'checkbox'}
                checked={contactIsMe}
                onChange={(event) => {
                  if (event.target.checked) {
                    setContact(user?.email || '');
                  }
                  setContactIsMe(event.target.checked);
                }}
              />
              {t('iAmTheOneWhoTakesCareOfIt')}
            </label>
          </div>

          <Button className={'mb-3 w-full'} type={'button'} variant={'confirm'} onClick={save}>
            {t('finishInitialSetting')}
          </Button>

          <button className={'mb-3 w-full rounded-md border bg-white-100 px-3 py-2 shadow-sm hover:bg-gray-50'} type={'button'} onClick={() => back && back()}>
            {t('back')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default InviteCollaborators;
